import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import auth from '../utils/auth';
import localCart from '../utils/localCartStorage';
import '../styles/ShoppingCart.css';
import { CartContext } from '../contexts/CartContext';
import { scrollToTop } from '../hooks/useScrollToTop';

const ShoppingCart = ({ onCartEmptyChange }) => {
    const [cartItems, setCartItems] = useState([]);
    const { updateCartCount } = useContext(CartContext);
    const navigate = useNavigate();
    const isAuthenticated = auth.isAuthenticated();

    const fetchCartItems = useCallback(async () => {
        try {
            let items = [];
            let total = 0;

            if (isAuthenticated) {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cart`, {
                    headers: {
                        'Authorization': `Bearer ${auth.getToken()}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    items = data.items;
                    total = items.reduce((sum, item) => sum + item.quantity, 0);
                }
            } else {
                const cart = localCart.getCart();
                items = cart.items;
                total = items.reduce((sum, item) => sum + item.quantity, 0);
            }

            setCartItems(items);
            updateCartCount(total);

            if (onCartEmptyChange) {
                onCartEmptyChange(items.length === 0);
            }
        } catch (error) {
            console.error('Error fetching cart items:', error);
            if (onCartEmptyChange) {
                onCartEmptyChange(true);
            }
        }
    }, [isAuthenticated, updateCartCount, onCartEmptyChange]);

    useEffect(() => {
        fetchCartItems();
    }, [fetchCartItems]);

    const handleQuantityChange = async (productId, newQuantity) => {
        if (newQuantity < 1 || newQuantity > 10) {
            alert('Kvantiteten måste vara mellan 1 och 10.');
            return;
        }

        try {
            if (isAuthenticated) {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cart/update`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${auth.getToken()}`
                    },
                    body: JSON.stringify({ productId, quantity: newQuantity })
                });

                if (response.ok) {
                    await fetchCartItems();
                } else {
                    const errorData = await response.json();
                    alert(errorData.message || 'Ett fel uppstod vid uppdatering av kundvagnen.');
                }
            } else {
                localCart.updateCartItem(productId, newQuantity);
                await fetchCartItems();
            }
        } catch (error) {
            console.error('Error updating cart item:', error);
            alert(error.message || 'Ett fel uppstod vid uppdatering av kundvagnen.');
        }
    };

    const handleRemoveItem = async (productId) => {
        try {
            if (isAuthenticated) {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cart/remove`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${auth.getToken()}`
                    },
                    body: JSON.stringify({ productId })
                });

                if (response.ok) {
                    await fetchCartItems();
                    scrollToTop();
                } else {
                    alert('Ett fel uppstod vid borttagning av produkten från kundvagnen.');
                }
            } else {
                localCart.removeFromCart(productId);
                await fetchCartItems();
                scrollToTop();
            }
        } catch (error) {
            console.error('Error removing cart item:', error);
            alert('Ett fel uppstod vid borttagning av produkten från kundvagnen.');
        }
    };

    const navigateToProducts = (event) => {
        event.preventDefault();
        navigate('/');
        scrollToTop();
    };

    const subtotal = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
    const shippingCost = 65;
    const total = subtotal + (cartItems.length > 0 ? shippingCost : 0);

    return (
        <div className="shopping-cart">
            <h2>Din beställning</h2>
            {cartItems.length === 0 ? (
                <div className="empty-cart">
                    <p>Din kundvagn är tom.</p>
                    <button className="continue-shopping-btn" onClick={navigateToProducts}>
                        Fortsätt handla
                    </button>
                </div>
            ) : (
                <>
                    {cartItems.map((item) => (
                        <div key={item.productId} className="cart-item">
                            <div className="cart-item-info">
                                <span className="cart-item-name">
                                    <span className="quantity-indicator">({item.quantity}<span className="quantity-x">x</span>)</span> {item.productId}
                                </span>
                                <span className="cart-item-price">Pris: {item.price} kr</span>
                            </div>
                            <div className="cart-item-actions">
                                <div className="quantity-control">
                                    <button onClick={() => handleQuantityChange(item.productId, item.quantity - 1)} disabled={item.quantity <= 1}>-</button>
                                    <span>{item.quantity}</span>
                                    <button onClick={() => handleQuantityChange(item.productId, item.quantity + 1)} disabled={item.quantity >= 10}>+</button>
                                </div>
                                <button className="remove-button" onClick={() => handleRemoveItem(item.productId)}>Ta bort</button>
                            </div>
                        </div>
                    ))}
                    <div className="cart-summary">
                        <div className="cart-subtotal">
                            Delsumma: {subtotal} kr
                        </div>
                        <div className="cart-shipping">
                            Frakt: {shippingCost} kr
                        </div>
                        <div className="cart-total">
                            Totalt: {total} kr
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ShoppingCart;